import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, IconButton, rem, Stack, Text, XOutline } from '@bonusly/design-system';
import { ProductTier, useCurrentCompany, useCurrentUser, useFeatureFlag } from '@bonusly/who-am-i';

import { UserContentDismissal } from '../../../components/profile/user-content-dismissal';
import { IN_APP_FEATURE_ANNOUNCEMENTS_FLAG } from '../../../components/promo/dynamic-promo';
import { Page } from '../../../modules/bonus-feed/config/router';

const DISMISSAL_NAMESPACE = 'org_support';
const DISMISSAL_NAME = 'cs-office-hours-sidebar';

interface OrgSupportCardProps {
	isExpanded: boolean;
}

export const OrgSupportCard: FC<OrgSupportCardProps> = ({ isExpanded }) => {
	const { t } = useTranslation('navigation');
	const user = useCurrentUser();
	const company = useCurrentCompany();
	// Suppress the legacy office-hours card whenever the dynamic promo
	// system is on — its content is being replaced by data-driven promos.
	const isPromoSystemOn = useFeatureFlag(IN_APP_FEATURE_ANNOUNCEMENTS_FLAG);

	const isActiveOrg = company.status === 'active' && company.product_tier === ProductTier.TierThree;

	if (isPromoSystemOn || !isExpanded || !user?.admin || !isActiveOrg || company.is_superadmin_initialized) return null;

	return (
		<UserContentDismissal name={DISMISSAL_NAME} namespace={DISMISSAL_NAMESPACE}>
			{(dismiss) => (
				<Stack
					bgColor="bg.surface"
					borderRadius="md"
					border="1px solid"
					borderColor="border.default"
					p={4}
					my={2}
					spacing={2}
					overflow="hidden"
					opacity={isExpanded ? 1 : 0}
					transition="opacity 0.15s cubic-bezier(.4, 0, .2, 1)"
					position="relative"
				>
					<Box position="absolute" top={1} right={1}>
						<IconButton aria-label={t('support_card.dismiss')} icon={<XOutline boxSize={rem(14)} />} size="xs" variant="ghost" onClick={dismiss} />
					</Box>
					<Text fontSize={14} fontWeight="semibold" color="text.default" whiteSpace="nowrap">
						{t('support_card.title')}
					</Text>
					<Text fontSize={14} color="text.softer">
						{t('support_card.subtitle')}
					</Text>
					<Button as="a" href={Page.CsOfficeHours} target="_blank" variant="solid" colorScheme="secondary" whiteSpace="nowrap">
						{t('support_card.office_hours_button')}
					</Button>
				</Stack>
			)}
		</UserContentDismissal>
	);
};
